import React, { useState } from 'react';
import propTypes from 'prop-types';
import { Grid, Button, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import * as Sentry from '@sentry/browser';
import ReCAPTCHA from 'react-google-recaptcha';
import PasswordInput from './PasswordInput';
import ConfirmPasswordInput from './ConfirmPasswordInput';
import {
  cleanSpaces, passwordValidation, codeValidation, getUserName, getCode, confirmPasswordValidation,
} from '../../utils';
import { cognitoClientConfirmPassword, CognitoException, cognitoErrors } from '../../api';
import copies from './copies';
import { RECAPTCHA_KEY } from '../../constants';
import styles from './RecoveryForm.module.css';

const RecoveryForm = ({ setSuccess }) => {
  // eslint-disable-next-line no-restricted-globals
  const userName = getUserName(location.search);
  // eslint-disable-next-line no-restricted-globals
  const code = getCode(location.search);

  const [newPassword, setNewPassword] = useState('');
  const [confirmationPassword, setConfirmationPassword] = useState('');
  const [validationCode, setValidationCode] = useState(code);
  const [cognitoError, setCognitoError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [token, setToken] = useState('');

  const onSuccess = () => {
    setIsSubmitting(false);
    setSuccess(true);
  };

  const onFailure = (errorObject) => {
    const error = new CognitoException(errorObject.code, errorObject.message);
    let errorLevel;
    switch (error.name) {
      case cognitoErrors.LimitExceededException:
        setErrorMessage(copies.limitErrorMessage);
        errorLevel = Sentry.Severity.Warning;
        break;
      case cognitoErrors.CodeMismatchException:
        setErrorMessage(copies.codeErrorMessage);
        setValidationCode('');
        errorLevel = Sentry.Severity.Warning;
        break;
      case cognitoErrors.ExpiredCodeException:
        setErrorMessage(copies.codeExpiredMessage);
        setNewPassword('');
        setConfirmationPassword('');
        setValidationCode('');
        errorLevel = Sentry.Severity.Warning;
        break;
      default:
        setErrorMessage(copies.errorDefault);
        errorLevel = Sentry.Severity.Error;
        break;
    }
    Sentry.withScope((scope) => {
      scope.setLevel(errorLevel);
      scope.setUser({
        id: userName,
      });
      Sentry.captureException(error);
    });
    setIsSubmitting(false);
    setCognitoError(true);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setCognitoError(false);
    cognitoClientConfirmPassword(validationCode, newPassword, userName, onSuccess, onFailure);
  };

  const cleanPassword = (event) => {
    const cleanValue = cleanSpaces(event.target.value);
    setNewPassword(cleanValue);
  };

  const cleanConfirmationPassword = (event) => {
    const cleanValue = cleanSpaces(event.target.value);
    setConfirmationPassword(cleanValue);
  };

  const cleanValidationCode = (event) => {
    const cleanValue = cleanSpaces(event.target.value);
    setValidationCode(cleanValue);
  };

  const updateToken = (value) => {
    setToken(value);
  };

  const buttonIsDisabled = !(
    passwordValidation(newPassword)
    && codeValidation(validationCode)
    && confirmPasswordValidation(newPassword, confirmationPassword)
    && !!token
  ) || isSubmitting;

  return (
    <div>
      <form
        id="validator-form"
        onSubmit={onSubmit}
      >
        <Grid item xs={12}>
          <p id="helper-text" className={styles.paragraph}>{copies.message}</p>
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="input-verification-code"
            name="verification-code"
            label={copies.verificationCodeLabel}
            className={styles.input}
            variant="filled"
            type="number"
            error={validationCode.length >= 1 && !codeValidation(validationCode)}
            helperText={copies.verificationCodeRegexValidationError}
            InputProps={{ inputProps: { inputMode: 'numeric' } }}
            value={validationCode}
            onChange={cleanValidationCode}
          />
        </Grid>
        <Grid item xs={12}>
          <PasswordInput
            id="input-password"
            name="new-password"
            isSubmitting={isSubmitting}
            onChange={cleanPassword}
            className={styles.input}
            label={copies.newPasswordLabel}
            value={newPassword}
          />
        </Grid>
        <Grid item xs={12}>
          <ConfirmPasswordInput
            id="input-confirm-password"
            name="confirm-password"
            isSubmitting={isSubmitting}
            onChange={cleanConfirmationPassword}
            className={styles.input}
            label={copies.confirmPasswordLabel}
            variant="filled"
            value={confirmationPassword}
            password={newPassword}
          />
        </Grid>
        {cognitoError && (
          <Grid item xs={12}>
            <div className={styles.alert}>
              <Alert
                variant="outlined"
                severity="error"
                onClose={() => { setCognitoError(false); }}
              >
                {errorMessage}
              </Alert>
            </div>
          </Grid>
        )}
        <Grid item className={styles.centered} xs={12}>
          <ReCAPTCHA
            sitekey={RECAPTCHA_KEY}
            onChange={updateToken}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            className={styles.button}
            variant="contained"
            id="submit-button"
            type="submit"
            disabled={buttonIsDisabled}
          >
            {copies.submit}
          </Button>
        </Grid>

      </form>
    </div>
  );
};
RecoveryForm.propTypes = {
  setSuccess: propTypes.func.isRequired,
};
export default RecoveryForm;
